import { useCallback, useState } from "react";
import { iconSizes } from "@stubhub/broadway-types";
import { Heading, Modal, ModalBody, ModalFooter, ModalHeader, ModalMobileVariant } from "@stubhub/broadway-web";
import { StarFillIcon } from "@stubhub/broadway-web/icons/functional";
import { useContent, useFormattedContent } from "src/shared/components/content";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useIsStubhub } from "src/shared/utils/appContextHelper";
import { APP_STORE_IDS, APP_STORE_LINKS, COBRAND_IDENTIFIERS } from "src/website-apps/shared/constants/app-install";
import { AppleStoreLink } from "../app-install-banner/components/AppleStoreLink";
import { GooglePlayStoreLink } from "../app-install-banner/components/GooglePlayStoreLink";
import { QRCode } from "../app-install-banner/components/QRCode";
import {
  appIconStyle,
  bodyStyle,
  footerStyle,
  headingStyle,
  modalCardClass,
  orDividerStyle,
  ratingCardStyle,
  ratingGroupStyle,
  ratingGroupWrapperStyle,
  ratingLabelStyle,
  ratingScoreStyle,
  ratingStarsStyle,
  storeBadgesRowStyle,
} from "./AppInstallInterstitialDesktop.styles";
import type { AppInstallInterstitialPlacement } from "./types";
import { useAppInstallInterstitialStorage } from "./useAppInstallInterstitialStorage";
import { placementToLinkType } from "./utils";

const STAR_COUNT = 5;

// ─── Props ───────────────────────────────────────────────────────────────────

interface AppInstallInterstitialDesktopProps {
  placement: AppInstallInterstitialPlacement;
}

/**
 * App install interstitial — Desktop/tablet variant.
 *
 * Centered modal with gradient blob, rating card + app icon group,
 * headline text, QR code, and App Store / Google Play buttons.
 *
 * Manages its own show/hide storage state — callers (e.g. AddFeature) only need
 * to mount this component unconditionally for variant users.
 *
 * Figma: https://www.figma.com/design/TBcCjPU38IObny3cJOFDQ5/App-Downloads?node-id=400-47619
 * JIRA:  https://thestubhub.atlassian.net/browse/CXWD-3699
 */
export const AppInstallInterstitialDesktop = ({ placement }: AppInstallInterstitialDesktopProps) => {
  const appContext = useAppContext();
  const storePageEvent = useStorePageEvent();
  const { shouldShow, markDismissed, markConverted } = useAppInstallInterstitialStorage();
  const [isOpen, setIsOpen] = useState(true);
  const isSh = useIsStubhub();
  const cobrandName = isSh ? COBRAND_IDENTIFIERS.SH : COBRAND_IDENTIFIERS.VGG;
  const linkType = placementToLinkType(placement);
  // Use REACT_APP_ASSETS_URL so the correct CDN host is used per environment
  const appIconSrc = `${appContext.env["REACT_APP_ASSETS_URL"]}/images/Assets/Icons/bfx/stubhub-logo-app-install-interstitial-rotate.svg`;

  // cid$#4248106# — "Download the {cobrandName} app to buy, sell, and manage your tickets with ease"
  const titleContent = useFormattedContent("cid$#4248106#", [cobrandName]);
  // cid$#4248107# — "Or download from"
  const orDownloadFromContent = useContent("cid$#4248107#");
  // cid$#4250583# — "1.4m+ App Store Ratings"
  const ratingsLabelContent = useContent("cid$#4250583#");

  const appleAppUrl = APP_STORE_LINKS?.[cobrandName]?.APPLE;
  const googleAppUrl = APP_STORE_LINKS?.[cobrandName]?.GOOGLE;

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        return;
      }

      setIsOpen(false);
      storePageEvent(PE_TYPE.CLOSE, PE_KEYS.APP_INSTALL_INTERSTITIAL_DISMISS, "");
      markDismissed();
    },
    [markDismissed, storePageEvent],
  );

  const handleCtaClick = useCallback(
    (storeId: string) => {
      storePageEvent(
        PE_TYPE.CLICK,
        PE_KEYS.APP_INSTALL_INTERSTITIAL_CTA_CLICK,
        `${cobrandName}_${storeId}_${placement}`,
      );
      markConverted();
    },
    [cobrandName, placement, markConverted, storePageEvent],
  );

  if (!shouldShow) return null;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      dismissible
      mobileVariant={ModalMobileVariant.Dialog}
      classes={{ content: modalCardClass }}
    >
      <ModalHeader />
      <ModalBody>
        <div className={bodyStyle}>
          {/* Rating card + app icon group — no gap between group and label */}
          <div className={ratingGroupWrapperStyle}>
            <div className={ratingGroupStyle}>
              <div className={ratingCardStyle}>
                <span className={ratingScoreStyle} aria-label="4.8 stars">
                  4.8
                </span>
                <div className={ratingStarsStyle}>
                  {Array.from({ length: STAR_COUNT }, (_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: static fixed-length list
                    <StarFillIcon key={i} size={iconSizes[0]} />
                  ))}
                </div>
              </div>
              <img src={appIconSrc} alt="" aria-hidden="true" className={appIconStyle} />
            </div>
            <span className={ratingLabelStyle}>{ratingsLabelContent}</span>
          </div>

          {/* Headline — 284px wide to produce 2-line wrap at 400px modal width */}
          <Heading variant="header" className={headingStyle}>
            {titleContent}
          </Heading>

          {/* QR code */}
          <QRCode isSh={isSh} type={linkType} />
        </div>
      </ModalBody>
      <ModalFooter>
        <div className={footerStyle}>
          <span className={orDividerStyle}>{orDownloadFromContent}</span>
          <div className={storeBadgesRowStyle}>
            <AppleStoreLink
              languageCode={appContext.languageCode}
              linkUrl={appleAppUrl}
              onClick={() => handleCtaClick(APP_STORE_IDS.APPLE)}
            />
            <GooglePlayStoreLink
              languageCode={appContext.languageCode}
              linkUrl={googleAppUrl}
              onClick={() => handleCtaClick(APP_STORE_IDS.GOOGLE)}
            />
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
};
